import {t} from "@lingui/core/macro";
import * as React from "react";

import type {StyleType} from "@khanacademy/wonder-blocks-core";
import {
    usePublishAnalyticsEvent,
    type MinorConversionData,
} from "@khan/analytics-learning-time";
import {
    LoginButton,
    roleToUserRole,
    type LoginTypeData,
    type Role,
} from "@khan/auth-shared";
import {useMutation} from "@khan/data-gql";
import {redirect} from "@khan/urls";

import type {loginWithMicrosoftMutationType} from "./__graphql-types__/index.ts";
import {MICROSOFT_LOGIN_MUTATION} from "./microsoft-graphql.ts";
import {MicrosoftIcon} from "./microsoft-icon.tsx";

export interface CompleteLoginOverrideParamsMicrosoft {
    userData: LoginTypeData;
}

interface Props {
    disabled?: boolean;
    style?: StyleType;
    size?: "small" | "medium" | "large";
    linkClever?: boolean;
    continueUrl?: string;
    role?: Role;
    classCodes?: Array<string>;
    isSignup?: boolean;
    // Callback for when doLogin succeeds, overrides the completeLogin behavior
    //
    // If this is not provided, completeLogin will be executed as normal
    //
    // If this is provided, the passed function will be executed instead of completeLogin
    //
    // If your override function requires additional parameters, add them to the CompleteLoginOverrideParams type
    completeLoginOverride?: (
        options: CompleteLoginOverrideParamsMicrosoft,
    ) => Promise<void>;
    /**
     * Callback for when the login fails.
     *
     * This can be caused due to Sign in with Apple auth errors or errors
     * caused in the the Sign in with Apple GraphQL mutation.
     */
    onError?: (errorMessage: string) => unknown;
    // The handler to be executed when reauthentication is successful (to be
    // set only if the button is used for reauth)
    onReauthSuccess?: (arg1: string) => unknown;
    /**
     * Callback for when the reauth flow fails.
     *
     * This is executed when the re-authentication with Apple fails.
     */
    onReauthError?: (url: string) => unknown;
    /**
     * Event Type for the CEDAR event which will be published on click
     *
     * We only pass the event type and rely on this component to determine
     * the rest of the event data.
     *
     * NOTE: If you update the list of possible values for this type, make sure
     * to update `handleClick` to publish the correct event.
     */
    cedarEventType?: "SignupMicrosoftButtonClicked";
    iconOnly?: boolean;
    minorConversions?: ReadonlyArray<MinorConversionData>;
}

export function MicrosoftButton(props: Props): React.ReactNode {
    const {cedarEventType, continueUrl, isSignup, onError, role} = props;
    const [getLoginRedirect] = useMutation(MICROSOFT_LOGIN_MUTATION);
    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    const handleClick = React.useCallback(async () => {
        if (cedarEventType === "SignupMicrosoftButtonClicked") {
            publishAnalyticsEvent({
                eventType: "SignupMicrosoftButtonClicked",
                eventSchemaVersion: 1,
            });
        }

        const variables: loginWithMicrosoftMutationType["variables"] = {
            action: isSignup ? "SIGNUP" : "LOGIN",
            continueUrl,
            role: roleToUserRole(role),
        };

        const result = await getLoginRedirect(variables);
        if (
            !result ||
            result.status !== "success" ||
            result.data.loginWithMicrosoft?.error
        ) {
            if (onError) {
                onError("Could not get login URL from Microsoft.");
            }
            return;
        }
        if (result.data.loginWithMicrosoft?.redirectUrl) {
            redirect(result.data.loginWithMicrosoft?.redirectUrl);
        }
    }, [
        cedarEventType,
        continueUrl,
        getLoginRedirect,
        isSignup,
        onError,
        publishAnalyticsEvent,
        role,
    ]);

    return (
        <LoginButton
            title={t`Continue with Microsoft`}
            iconComponent={MicrosoftIcon}
            disabled={props.disabled}
            onClick={handleClick}
            style={props.style}
            size={props.size}
            iconOnly={props.iconOnly}
        />
    );
}
